import { createClient } from '@supabase/supabase-js';

// 从环境变量获取Supabase配置
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://你的项目URL.supabase.co';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '你的匿名密钥';

// 如果使用默认值，显示警告
if (supabaseUrl === 'https://你的项目URL.supabase.co' || 
    supabaseAnonKey === '你的匿名密钥') {
  console.warn('警告: 使用Supabase默认配置！应用功能可能受限。');
  console.warn('请在.env文件中设置REACT_APP_SUPABASE_URL和REACT_APP_SUPABASE_ANON_KEY');
}

// 创建Supabase客户端
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// 用户注册
export async function signUp(email, password, metadata = {}) {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata
      }
    });
    
    return { data, error };
  } catch (error) {
    console.error('注册错误:', error);
    return { data: null, error };
  }
}

// 用户登录
export async function signIn(email, password) {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    return { data, error };
  } catch (error) {
    console.error('登录错误:', error);
    return { data: null, error };
  }
}

// 退出登录
export async function signOut() {
  try {
    const { error } = await supabase.auth.signOut();
    return { error };
  } catch (error) {
    console.error('退出登录错误:', error);
    return { error };
  }
}

// 获取当前用户
export async function getCurrentUser() {
  try {
    const { data, error } = await supabase.auth.getUser();
    if (error) {
      console.error('获取当前用户错误:', error);
      return { user: null, error };
    }
    return { user: data.user, error: null };
  } catch (error) {
    console.error('获取当前用户错误:', error);
    return { user: null, error };
  }
}

// 保存EQ测试结果
export async function saveEQTestResult(userId, testData) {
  try {
    if (!userId) {
      throw new Error('未提供用户ID');
    }
    
    const { data, error } = await supabase
      .from('eq_test_results')
      .insert([{
        user_id: userId,
        answers: testData,
        created_at: new Date().toISOString()
      }]);
    
    return { data, error };
  } catch (error) {
    console.error('保存测试结果错误:', error);
    return { data: null, error };
  }
}

// 保存个人问卷结果
export async function saveQuestionnaireResult(userId, questionnaireData) {
  try {
    if (!userId) {
      throw new Error('未提供用户ID');
    }
    
    const { data, error } = await supabase
      .from('personal_questionnaires')
      .insert([{
        user_id: userId,
        answers: questionnaireData,
        created_at: new Date().toISOString()
      }]);
    
    return { data, error };
  } catch (error) {
    console.error('保存问卷结果错误:', error);
    return { data: null, error };
  }
}

// 保存自定义测试结果
export async function saveCustomTestResult(userId, testData) {
  try {
    if (!userId) {
      throw new Error('未提供用户ID');
    }
    
    const { data, error } = await supabase
      .from('custom_test_results')
      .insert([{
        user_id: userId,
        answers: testData,
        created_at: new Date().toISOString()
      }]);
    
    return { data, error };
  } catch (error) {
    console.error('保存自定义测试结果错误:', error);
    return { data: null, error };
  }
}

// 保存分析报告
export async function saveAnalysisReport(userId, reportData) {
  try {
    if (!userId) {
      throw new Error('未提供用户ID');
    }
    
    const { data, error } = await supabase
      .from('analysis_reports')
      .insert([{
        user_id: userId,
        report_data: reportData,
        created_at: new Date().toISOString()
      }]);
    
    return { data, error };
  } catch (error) {
    console.error('保存分析报告错误:', error);
    return { data: null, error };
  }
}

// 获取用户最新的测试结果
export async function getLatestUserTestResults(userId) {
  try {
    if (!userId) {
      throw new Error('未提供用户ID');
    }
    
    console.log('查询Supabase数据，用户ID:', userId);
    
    // 获取最新的EQ测试结果
    const { data: eqData, error: eqError } = await supabase
      .from('eq_test_results')
      .select('answers, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1);
    
    console.log('EQ测试数据原始响应:', eqData);
    console.log('EQ测试错误:', eqError);
    
    // 获取最新的问卷结果
    const { data: qData, error: qError } = await supabase
      .from('personal_questionnaires')
      .select('answers, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1);
    
    console.log('问卷数据原始响应:', qData);
    console.log('问卷错误:', qError);
    
    // 获取最新的自定义测试结果
    const { data: ctData, error: ctError } = await supabase
      .from('custom_test_results')
      .select('answers, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1);
    
    console.log('自定义测试数据原始响应:', ctData);
    console.log('自定义测试错误:', ctError);
    
    // 设置返回结果并记录详细日志
    const eqTestResult = eqData && eqData.length > 0 ? eqData[0].answers : null;
    const questionnaireResult = qData && qData.length > 0 ? qData[0].answers : null;
    const customTestResult = ctData && ctData.length > 0 ? ctData[0].answers : null;
    
    console.log('提取的EQ测试数据:', eqTestResult);
    console.log('提取的问卷数据:', questionnaireResult);
    console.log('提取的自定义测试数据:', customTestResult);
    
    const result = {
      data: {
        eqTestResult,
        questionnaireResult,
        customTestResult
      },
      error: eqError || qError || ctError
    };
    
    console.log('返回给组件的最终结果:', result);
    return result;
  } catch (error) {
    console.error('获取用户测试结果错误:', error);
    return { data: {}, error };
  }
}

// 获取用户最新的分析报告
export async function getLatestAnalysisReport(userId) {
  try {
    if (!userId) {
      throw new Error('未提供用户ID');
    }
    
    const { data, error } = await supabase
      .from('analysis_reports')
      .select('report_data, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1);
    
    return {
      data: data && data.length > 0 ? data[0].report_data : null,
      error
    };
  } catch (error) {
    console.error('获取分析报告错误:', error);
    return { data: null, error };
  }
}

// 更新用户元数据
export async function updateUserMetadata(metadata) {
  try {
    const { data, error } = await supabase.auth.updateUser({
      data: metadata
    });
    
    return { data, error };
  } catch (error) {
    console.error('更新用户元数据错误:', error);
    return { data: null, error };
  }
}

export default supabase; 